import { AnimatePresence, motion } from "motion/react";
import { FC, useMemo } from "react";
import { useRecoilValue } from "recoil";

import { RideStationsPanel, RideStationsPanelGreyed } from "@ryde/controls";
import { RouteBlock } from "@ryde/layout";
import { useRideRoutes, useRidesNavigation } from "@ryde/redux";
import {
  apiDataFetchingAtom,
  IRideDto,
  isRideGreyed,
  LiveMapDisplayModeEnum,
  MapRouteBlockTypeEnum,
  RideStationPanelMode,
} from "@ryde/shared";

import { detailPanelContainerAnimations } from "./ride.detail-panel.animations";
import {
  RideDetailsMapStyled,
  RideDetailsPanelContainerStyled,
  RideDetailsPanelStyled,
} from "./ride.details-panel.styled";

interface IRideDetailsPanelProps {
  rideDto: IRideDto;
}

export const RideDetailsPanel: FC<IRideDetailsPanelProps> = ({ rideDto }) => {
  const { rideRoutes } = useRideRoutes(rideDto);
  const dataLoading = useRecoilValue(apiDataFetchingAtom);
  const { isRidesEditRoute, isRidesAddRoute } = useRidesNavigation();
  const isGreyed = isRideGreyed(rideDto) || isRidesEditRoute;
  const mapHiddenStateCondition = useMemo(() => {
    return Boolean(dataLoading);
  }, [dataLoading]);

  const isMapCoveredByRightDrawer = useMemo(() => {
    return Boolean(isRidesEditRoute || isRidesAddRoute);
  }, [isRidesAddRoute, isRidesEditRoute]);

  return (
    <AnimatePresence>
      <RideDetailsPanelContainerStyled
        as={motion.div}
        variants={detailPanelContainerAnimations}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        {rideDto && (
          <RideDetailsPanelStyled>
            {!isGreyed && (
              <RideStationsPanel
                rideDto={rideDto}
                mode={RideStationPanelMode.GRID_PANEL}
              />
            )}
            {isGreyed && (
              <RideStationsPanelGreyed
                rideDto={rideDto}
                mode={RideStationPanelMode.GRID_PANEL}
              />
            )}

            {!mapHiddenStateCondition && (
              <RideDetailsMapStyled>
                {/* Single-ride view: pinned to full display, independent of the live-map toggle. Do
                    NOT switch to the shared live-map display mode — a single ride here must not depend
                    on (or change) how the many rides on the live map are rendered. */}
                <RouteBlock
                  isMapCoveredByRightDrawer={isMapCoveredByRightDrawer}
                  hiddenMapState={mapHiddenStateCondition}
                  routes={rideRoutes}
                  rideDto={rideDto}
                  routeBlockType={MapRouteBlockTypeEnum.RideDetailPanel}
                  liveMapDisplayMode={LiveMapDisplayModeEnum.stationPins}
                />
              </RideDetailsMapStyled>
            )}
          </RideDetailsPanelStyled>
        )}
      </RideDetailsPanelContainerStyled>
    </AnimatePresence>
  );
};
